import * as React from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import axios from "axios";
import { useState, useEffect, useCallback } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { appConstants } from "../_constants/app.constants";
import { useConfirm } from "../common/ConfirmDialog";

/**
 * Alta y gestión de colaboradores (Admin / operador de mesa).
 * Enlazada desde «Acciones del torneo» y desde Configurar torneo.
 */
export default function TournamentCollaboratorsPage() {
  const { tournamentId } = useParams();
  const confirm = useConfirm();
  const [tournamentName, setTournamentName] = useState("");
  const [access, setAccess] = useState(null);
  const [members, setMembers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("LiveOperator");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [loadError, setLoadError] = useState("");

  const API = appConstants.API_URL;

  const loadMembers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/tournaments/${tournamentId}/members`);
      setMembers(res.data || []);
    } catch {
      setMembers([]);
    }
  }, [API, tournamentId]);

  const refresh = useCallback(async () => {
    setLoadError("");
    try {
      const [tRes, aRes] = await Promise.all([
        axios.get(`${API}/api/tournaments/${tournamentId}`),
        axios.get(`${API}/api/tournaments/${tournamentId}/access`),
      ]);
      setTournamentName(tRes.data?.name || tRes.data?.Name || "Torneo");
      setAccess(aRes.data);
      if (aRes.data?.canManage) await loadMembers();
    } catch (e) {
      setLoadError("No se pudo cargar el torneo o no tienes permiso.");
      setAccess(null);
    }
  }, [API, tournamentId, loadMembers]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addMember = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setBusy(true);
    setMsg("");
    try {
      await axios.post(`${API}/api/tournaments/${tournamentId}/members`, {
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      setInviteEmail("");
      await loadMembers();
      setMsg("Usuario añadido o actualizado.");
    } catch (err) {
      setMsg(err.response?.data?.message || "No se pudo añadir.");
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (userId) => {
    if (!(await confirm({
      title: "Quitar colaborador",
      message: "¿Quitar a esta persona del torneo? Perderá el acceso a su gestión.",
      confirmText: "Quitar",
      tone: "danger",
    }))) return;
    setBusy(true);
    setMsg("");
    try {
      await axios.delete(`${API}/api/tournaments/${tournamentId}/members/${userId}`);
      await loadMembers();
    } catch (err) {
      setMsg(err.response?.data?.message || "No se pudo quitar.");
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <div className="w-full max-w-lg mx-auto px-4 py-10">
        <RouterLink
          to={`/tournamentactions/${tournamentId}`}
          className="inline-flex items-center gap-2 text-sm font-semibold text-ink-muted mb-6"
        >
          <ArrowBackIcon fontSize="small" />
          Volver al torneo
        </RouterLink>
        <p className="text-red-700 bg-red-50 border border-red-100 rounded-2xl px-4 py-3">{loadError}</p>
      </div>
    );
  }

  if (!access?.canManage) {
    return (
      <div className="w-full max-w-lg mx-auto px-4 py-10">
        <RouterLink
          to={`/tournamentactions/${tournamentId}`}
          className="inline-flex items-center gap-2 text-sm font-semibold text-ink-muted mb-6"
        >
          <ArrowBackIcon fontSize="small" />
          Volver al torneo
        </RouterLink>
        <div className="rounded-2xl border border-arena/15 bg-white/90 p-6 shadow-sm">
          <h1 className="font-display font-bold text-xl text-ink mb-2">Colaboradores</h1>
          <p className="text-sm text-ink-muted">
            Solo los <strong>administradores</strong> del torneo pueden invitar colaboradores. Si necesitas permisos,
            pide al organizador que te añada como Admin, operador de mesa o pantalla (Viewer).
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-lg mx-auto px-4 py-8 sm:py-10">
      <RouterLink
        to={`/tournamentactions/${tournamentId}`}
        className="inline-flex items-center gap-2 text-sm font-semibold text-ink-muted hover:text-arena mb-6 transition-colors"
      >
        <ArrowBackIcon fontSize="small" />
        Volver al torneo
      </RouterLink>

      <div className="rounded-3xl border border-arena/15 bg-white/95 p-6 sm:p-8 shadow-card">
        <div className="flex items-center gap-2 mb-1">
          <PersonAddIcon className="text-gold" />
          <h1 className="font-display font-extrabold text-2xl text-ink">Colaboradores</h1>
        </div>
        <p className="text-sm text-ink-muted mb-6">
          <span className="font-semibold text-ink">{tournamentName}</span>
          <br />
          Invita por <strong>email</strong> (la cuenta debe existir en la app).{" "}
          <strong>Admin</strong>: configura grupos, liga y eliminatoria.{" "}
          <strong>LiveOperator</strong>: solo marca resultados en la mesa.{" "}
          <strong>Viewer</strong>: solo abre el modo pantalla / TV (sin configurar ni mesa).
        </p>

        {msg && (
          <p className="text-sm mb-4 text-amber-900 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">{msg}</p>
        )}

        <ul className="text-sm space-y-2 mb-6 max-h-56 overflow-y-auto border border-arena/10 rounded-xl p-3 bg-slate-50/80">
          {members.length === 0 ? (
            <li className="text-ink-muted">Solo el organizador (owner) por ahora.</li>
          ) : (
            members.map((m) => (
              <li key={m.userId} className="flex justify-between gap-2 items-start border-b border-arena/10 last:border-0 pb-2 last:pb-0">
                <span>
                  <span className="font-semibold text-ink">{m.displayName || m.email}</span>
                  <span className="text-ink-muted text-xs block">{m.email}</span>
                  <span className="text-xs font-bold text-arena uppercase tracking-wide">{m.role}</span>
                </span>
                {m.role !== "Owner" && (
                  <button
                    type="button"
                    className="text-red-700 text-xs font-bold shrink-0"
                    disabled={busy}
                    onClick={() => removeMember(m.userId)}
                  >
                    Quitar
                  </button>
                )}
              </li>
            ))
          )}
        </ul>

        <form onSubmit={addMember} className="space-y-3">
          <label className="block text-xs font-bold text-ink-muted uppercase tracking-wide">Añadir por email</label>
          <input
            type="email"
            required
            placeholder="compañero@club.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            className="w-full rounded-xl border border-arena/25 px-3 py-2.5 text-sm"
          />
          <div className="flex flex-col sm:flex-row gap-2">
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              className="flex-1 rounded-xl border border-arena/25 px-3 py-2.5 text-sm"
            >
              <option value="Admin">Administrador (todo el torneo)</option>
              <option value="LiveOperator">Operador de mesa (solo marcador)</option>
              <option value="Viewer">Pantalla / visor TV (sin gestión)</option>
            </select>
            <button
              type="submit"
              disabled={busy}
              className="rounded-xl bg-arena px-5 py-2.5 text-sm font-bold text-white shadow-md hover:brightness-105 disabled:opacity-50"
            >
              Invitar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
